import { z } from 'zod'
import type {
  AddressingTypeSchema, BaseDeviceRequestSchema, CacheGroupSettingsSchema,
  DeviceBufferDataStorageTypeSchema, DeviceCreateRequestSchema, DeviceRegistrationStatus, DeviceSchema,
  DeviceStatusSchema, DeviceTagLevelSchema, DeviceTagSchema, DeviceTemplateKey, DeviceTypeSchema, DeviceUpdateRequestSchema,
  MappingInformationSchema, RealDeviceCreateRequestSchema, VirtualMappingInformationSchema
} from './DeviceValidation.js'

import type { DeviceTagType } from '~/components/devices/devices.js'
import type {
  AccountDirectLinkedDeviceSchema, ClientLinkedAccessGroupSchema,
  DeviceDirectLinkedClientSchema, LinkedAccessGroupSchema, LinkedDeviceSchema,
  SubscriptionPlanSchema
} from '~/types/Validation'
import type { SortDirection, TablePaginationRequest } from './GenericTable.js'

export const DeviceHexacodeSchema = z.string().regex(/[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}/).length(17)
export type DeviceHexacode = z.infer<typeof DeviceHexacodeSchema>
export type DeviceBufferDataStorageType = z.infer<typeof DeviceBufferDataStorageTypeSchema>
export type DeviceStatus = z.infer<typeof DeviceStatusSchema>
export type DeviceRegistrationStatus = z.infer<typeof DeviceRegistrationStatus>
export type DeviceType = z.infer<typeof DeviceTypeSchema>
export type AddressingType = z.infer<typeof AddressingTypeSchema>
export type DeviceTagMessage = 'MISSING_LICENSE' | 'BAD_CONNECTION' | 'WARN' | 'ERROR' | 'INFO'
export type DeviceTagLevel = z.infer<typeof DeviceTagLevelSchema>

export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>

export type SubscriptionPlanSaveRequest = {
  id: string
  name: string
  value: number
  linkedDevices: string[]
}

export type DeviceRedirectAddress = {
  domain: string
  address: string
}

/**
 * see DevicesFilter type
 */
export interface PaginationRequest {
  searchHint: string
  accountId: string | null
  tags: DeviceTagType[]
  dealer: string | null
  subDealer: string | null
  customer: string | null
  demo: boolean | null
  statuses: DeviceStatus[]
  type: DeviceType | null
  addressingType: AddressingType | null
  includedFields?: string[]
  registrationStatus: DeviceRegistrationStatus | null
  host: string | null
  optimabox?: boolean
  parentHexacode?: string | null
}

export interface DevicePaginationRequest extends TablePaginationRequest, PaginationRequest {
  excludedDevices?: string[]
  includedDevices?: string[]
}

export type DevicesOverviewPaginationResponse = {
  availableRealSites: number
  availableVirtualSites: number
  availableBusinessGroups: number
  connectedRealSites: number
  connectedVirtualSites: number
  connectedBusinessGroups: number
}

export type DeviceData = {
  id: string
  hexacode: string
  rawHexacode: number
  status: DeviceStatus
  type: DeviceType
  host: string
  name: string
  dealer: string
  subDealer: string
  customer: string
  createDate: string
  firstConnection: string
  connectedSince: string
  offlineSince: string
  ccId: string
  ubn: number | null
  additionalInformation: Record<string, string>
  tags: DeviceTag[]
  buffers: number
  assignedClientsCount: number
  assignedWebClientsCount: number
  subscriptionPlans: SubscriptionPlan[]
  mappings: VirtualMappingInformation[]
  addressingType: AddressingType
  ipAddress: string
  supportsAX: boolean
  supportsXfsLogs: boolean
  lastKnownTimezone: string
  revisionNumber: string
  versionNumber: string
  demoDevice: boolean
  registrationStatus: DeviceRegistrationStatus | null
  template: DeviceTemplateKey
}

export type ExistingHexacodesRequest = {
  hexacodes: string[]
}
export type DeviceTag = z.infer<typeof DeviceTagSchema>

export type ExportOption = {
  title: string
  value: string
}

export type PageLengthOption = {
  title: string
  value: number
}

export type Dealer = {
  id: string | null
  name: string
}

export type Subdealer = {
  id: string | null
  name: string
}

export type DeviceAlarmHistoryData = {
  departmentName: string
  translation: string
  code: number
  occuredAt: string
  occuredAtUtc: string
  clearedAt: string | null
  clearedAtUtc: string | null
  deviceName: string | null
  businessGroupName: string | null
}

export type VirtualMappingInformation = z.infer<typeof VirtualMappingInformationSchema>
export type MappingInformation = z.infer<typeof MappingInformationSchema>

export type DeviceBlackListEntry = {
  hexacode: string
  remoteIp: string
  timestamp: string
  location: Location
}

export type DeviceRedirectEntry = {
  hexacode: string
  redirectAddress: string
}

export type Location = {
  contry: string
  city: string
  latitude: number
  longitude: number
}

export type LinkedAccessGroup = z.infer<typeof LinkedAccessGroupSchema>
export type AccountDirectLinkedDevice = z.infer<typeof AccountDirectLinkedDeviceSchema>
export type DeviceDirectLinkedClient = z.infer<typeof DeviceDirectLinkedClientSchema>
export type ClientLinkedAccessGroup = z.infer<typeof ClientLinkedAccessGroupSchema>

export type ExportDeviceRequest = {
  type: ExportOption
  columns: string[]
  statuses: string[]
  dealer: string
  subDealer: string
  accountId: string
  demo: string
  sortField: string
  sortDirection: SortDirection
  searchHint: string
  tags: string[]
  addressingType: AddressingType
  deviceType: DeviceType
}

export type AssignedClientsSaveRequest = {
  hexacode: string
  permissions: Record<string, boolean>
}

export type DevicelineEntry = {
  status: boolean
  timestamp: string
}

export type Device = z.infer<typeof DeviceSchema>
export type DeviceCreateRequest = z.infer<typeof DeviceCreateRequestSchema>
export type DeviceUpdateRequest = z.infer<typeof DeviceUpdateRequestSchema>

export type BaseDeviceRequest = z.infer<typeof BaseDeviceRequestSchema>
export type RealDeviceCreateRequest = z.infer<typeof RealDeviceCreateRequestSchema>

export type CacheGroupSettings = z.infer<typeof CacheGroupSettingsSchema>

export type LinkedDevice = z.infer<typeof LinkedDeviceSchema>
